import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import ConsultationApi from "../../../../../infrastructure/api/consultation";

const systemStatusCodes = new Set(["new", "registereduser"]);

const isSystemStatus = (code) => systemStatusCodes.has(String(code || "").toLowerCase());

const TAG_COLOR_FALLBACK = "#1976d2";

/** مقدار معتبر برای input[type=color] (#rrggbb) */
const normalizeTagHex = (value) => {
  if (value == null || String(value).trim() === "") return TAG_COLOR_FALLBACK;
  let s = String(value).trim();
  if (!s.startsWith("#")) s = `#${s}`;
  if (/^#[0-9A-Fa-f]{6}$/.test(s)) return s.toLowerCase();
  if (/^#[0-9A-Fa-f]{3}$/.test(s)) {
    const r = s[1];
    const g = s[2];
    const b = s[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return TAG_COLOR_FALLBACK;
};

const PRESET_TAG_COLORS = [
  "#1976d2",
  "#2e7d32",
  "#ed6c02",
  "#9c27b0",
  "#d32f2f",
  "#00796b",
  "#5d4037",
  "#455a64",
  "#fbc02d",
  "#1565c0",
];

const TagColorField = ({ id, value, onChange }) => {
  const hex = normalizeTagHex(value);
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
      <Typography component="label" htmlFor={id} variant="body2" color="text.secondary" sx={{ alignSelf: "center" }}>
        رنگ
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, direction: "ltr" }}>
        <input
          id={id}
          type="color"
          value={hex}
          onChange={(e) => onChange(e.target.value)}
          aria-label="انتخاب رنگ از پالت"
          style={{
            width: 48,
            height: 36,
            padding: 0,
            border: "1px solid rgba(0, 0, 0, 0.23)",
            borderRadius: 4,
            cursor: "pointer",
            backgroundColor: "#fff",
          }}
        />
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, alignItems: "center", maxWidth: 280 }}>
        {PRESET_TAG_COLORS.map((c) => (
          <Box
            key={c}
            component="button"
            type="button"
            title={c}
            onClick={() => onChange(c)}
            aria-label={`رنگ پیشنهادی ${c}`}
            sx={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              bgcolor: c,
              border: "2px solid",
              borderColor: hex === c ? "primary.main" : "divider",
              cursor: "pointer",
              p: 0,
              flexShrink: 0,
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

const ConsultationSettingsPage = () => {
  const [registrationSmsText, setRegistrationSmsText] = useState("");
  const [completeProfileSmsText, setCompleteProfileSmsText] = useState("");
  const [defaultExpertId, setDefaultExpertId] = useState("");
  const [experts, setExperts] = useState([]);
  const [tags, setTags] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [newTagTitle, setNewTagTitle] = useState("");
  const [newTagColor, setNewTagColor] = useState("#1976d2");
  const [newStatusTitle, setNewStatusTitle] = useState("");
  const [newStatusCode, setNewStatusCode] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);
  const [tagDialog, setTagDialog] = useState(null);
  const [statusDialog, setStatusDialog] = useState(null);

  const load = useCallback(async () => {
    try {
      const [st, ex, tg, ss] = await Promise.all([
        ConsultationApi.getSettings(),
        ConsultationApi.getExperts(),
        ConsultationApi.getTags(),
        ConsultationApi.getStatuses(),
      ]);
      if (st?.isSuccess && st.data) {
        setRegistrationSmsText(st.data.registrationSmsText || "");
        setCompleteProfileSmsText(st.data.completeProfileSmsText || "");
        setDefaultExpertId(
          st.data.defaultAssignedExpertUserId != null
            ? String(st.data.defaultAssignedExpertUserId)
            : ""
        );
      }
      if (ex?.isSuccess) setExperts(ex.data || []);
      if (tg?.isSuccess) setTags(tg.data || []);
      if (ss?.isSuccess) setStatuses(ss.data || []);
    } catch {
      toast.error("خطا در بارگذاری");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await ConsultationApi.saveSettings({
        registrationSmsText,
        completeProfileSmsText,
        defaultAssignedExpertUserId: defaultExpertId
          ? Number(defaultExpertId)
          : null,
      });
      if (res?.isSuccess) toast.success("ذخیره شد");
      else toast.error(res?.message || "خطا");
    } catch {
      toast.error("خطا در ذخیره تنظیمات");
    } finally {
      setSavingSettings(false);
    }
  };

  const addTag = async () => {
    if (!newTagTitle.trim()) return;
    try {
      const res = await ConsultationApi.saveTag({
        id: 0,
        title: newTagTitle.trim(),
        color: normalizeTagHex(newTagColor),
        isActive: true,
      });
      if (res?.isSuccess) {
        toast.success("تگ اضافه شد");
        setNewTagTitle("");
        await load();
      } else toast.error(res?.message || "خطا");
    } catch {
      toast.error("خطا در ذخیره تگ");
    }
  };

  const addStatus = async () => {
    if (!newStatusTitle.trim() || !newStatusCode.trim()) return;
    try {
      const res = await ConsultationApi.saveStatus({
        id: 0,
        title: newStatusTitle.trim(),
        code: newStatusCode.trim(),
        sortOrder: 200,
        isActive: true,
      });
      if (res?.isSuccess) {
        toast.success("وضعیت اضافه شد");
        setNewStatusTitle("");
        setNewStatusCode("");
        await load();
      } else toast.error(res?.message || "خطا");
    } catch {
      toast.error("خطا در ذخیره وضعیت");
    }
  };

  const saveTagDialog = async () => {
    if (!tagDialog?.title?.trim()) {
      toast.error("عنوان الزامی است");
      return;
    }
    try {
      const res = await ConsultationApi.saveTag({
        id: tagDialog.id,
        title: tagDialog.title.trim(),
        color: normalizeTagHex(tagDialog.color),
        isActive: !!tagDialog.isActive,
      });
      if (res?.isSuccess) {
        toast.success("تگ به‌روز شد");
        setTagDialog(null);
        await load();
      } else toast.error(res?.message || "خطا");
    } catch {
      toast.error("خطا در ذخیره");
    }
  };

  const saveStatusDialog = async () => {
    if (!statusDialog?.title?.trim() || !statusDialog?.code?.trim()) {
      toast.error("عنوان و کد الزامی است");
      return;
    }
    try {
      const res = await ConsultationApi.saveStatus({
        id: statusDialog.id,
        title: statusDialog.title.trim(),
        code: statusDialog.code.trim(),
        sortOrder: Number(statusDialog.sortOrder) || 0,
        isActive: !!statusDialog.isActive,
      });
      if (res?.isSuccess) {
        toast.success("وضعیت به‌روز شد");
        setStatusDialog(null);
        await load();
      } else toast.error(res?.message || "خطا");
    } catch {
      toast.error("خطا در ذخیره");
    }
  };

  const tagRow = (t) => ({
    id: t.id ?? t.Id,
    title: t.title ?? t.Title ?? "",
    color: normalizeTagHex(t.color ?? t.Color ?? TAG_COLOR_FALLBACK),
    isActive: t.isActive ?? t.IsActive ?? true,
  });

  const statusRow = (s) => ({
    id: s.id ?? s.Id,
    title: s.title ?? s.Title ?? "",
    code: s.code ?? s.Code ?? "",
    sortOrder: s.sortOrder ?? s.SortOrder ?? 0,
    isActive: s.isActive ?? s.IsActive ?? true,
  });

  const confirmDeleteTag = async (row) => {
    if (!window.confirm(`حذف تگ «${row.title}»؟ ارتباط این تگ با درخواست‌ها هم حذف می‌شود.`)) return;
    try {
      const res = await ConsultationApi.deleteTag(row.id);
      if (res?.isSuccess) {
        toast.success("حذف شد");
        await load();
      } else toast.error(res?.message || "خطا");
    } catch {
      toast.error("خطا در حذف");
    }
  };

  const confirmDeleteStatus = async (row) => {
    if (isSystemStatus(row.code)) return;
    if (!window.confirm(`حذف وضعیت «${row.title}»؟`)) return;
    try {
      const res = await ConsultationApi.deleteStatus(row.id);
      if (res?.isSuccess) {
        toast.success("حذف شد");
        await load();
      } else toast.error(res?.message || "خطا");
    } catch {
      toast.error("خطا در حذف");
    }
  };

  return (
    <Box sx={{ p: 2, maxWidth: 960 }}>
      <Typography variant="h5" gutterBottom>
        تنظیمات مشاوره
      </Typography>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          متن پیامک‌ها و ارجاع پیش‌فرض
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="متن پیامک پس از ثبت درخواست عمومی"
          helperText="لینک پیشنهادی: https://app.solarent.ir/auth/login — از /landing استفاده نکنید."
          value={registrationSmsText}
          onChange={(e) => setRegistrationSmsText(e.target.value)}
          margin="normal"
        />
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="متن پیامک دعوت به تکمیل اطلاعات (تبدیل به کاربر)"
          helperText="لینک ورود: https://app.solarent.ir/auth/login"
          value={completeProfileSmsText}
          onChange={(e) => setCompleteProfileSmsText(e.target.value)}
          margin="normal"
        />
        <FormControl fullWidth margin="normal">
          <InputLabel>کارشناس پیش‌فرض</InputLabel>
          <Select
            label="کارشناس پیش‌فرض"
            value={defaultExpertId}
            onChange={(e) => setDefaultExpertId(e.target.value)}
          >
            <MenuItem value="">—</MenuItem>
            {experts.map((u) => (
              <MenuItem key={u.id} value={String(u.id)}>
                {u.fullName || u.FullName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          type="button"
          disabled={savingSettings}
          onClick={saveSettings}
          sx={{ mt: 1 }}
        >
          {savingSettings ? "در حال ذخیره…" : "ذخیره تنظیمات"}
        </Button>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          تگ‌ها
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", mb: 2 }}>
          <TextField
            size="small"
            label="عنوان تگ جدید"
            value={newTagTitle}
            onChange={(e) => setNewTagTitle(e.target.value)}
          />
          <TagColorField id="new-consultation-tag-color" value={newTagColor} onChange={setNewTagColor} />
          <Button variant="outlined" type="button" onClick={addTag}>
            افزودن تگ
          </Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>شناسه</TableCell>
                <TableCell>عنوان</TableCell>
                <TableCell>رنگ</TableCell>
                <TableCell>فعال</TableCell>
                <TableCell align="left" width={100}>
                  عملیات
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tags.map((x) => {
                const t = tagRow(x);
                return (
                <TableRow key={t.id}>
                  <TableCell>{t.id}</TableCell>
                  <TableCell>{t.title}</TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: 1,
                        bgcolor: t.color || "#ccc",
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    />
                  </TableCell>
                  <TableCell>{t.isActive ? "بله" : "خیر"}</TableCell>
                  <TableCell align="left">
                    <IconButton
                      size="small"
                      color="primary"
                      aria-label="ویرایش"
                      onClick={() =>
                        setTagDialog({
                          id: t.id,
                          title: t.title || "",
                          color: normalizeTagHex(t.color),
                          isActive: !!t.isActive,
                        })
                      }
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      aria-label="حذف"
                      onClick={() => confirmDeleteTag(t)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
              })}
              {tags.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary">
                      تگی ثبت نشده است.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          وضعیت‌ها (کد یکتا لاتین)
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", mb: 2 }}>
          <TextField
            size="small"
            label="عنوان"
            value={newStatusTitle}
            onChange={(e) => setNewStatusTitle(e.target.value)}
          />
          <TextField
            size="small"
            label="Code"
            value={newStatusCode}
            onChange={(e) => setNewStatusCode(e.target.value)}
            sx={{ width: 160 }}
          />
          <Button variant="outlined" type="button" onClick={addStatus}>
            افزودن وضعیت
          </Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>شناسه</TableCell>
                <TableCell>عنوان</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>ترتیب</TableCell>
                <TableCell>فعال</TableCell>
                <TableCell align="left" width={100}>
                  عملیات
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {statuses.map((x) => {
                const s = statusRow(x);
                return (
                <TableRow key={s.id}>
                  <TableCell>{s.id}</TableCell>
                  <TableCell>{s.title}</TableCell>
                  <TableCell>
                    <Typography component="span" variant="body2" sx={{ direction: "ltr", unicodeBidi: "embed" }}>
                      {s.code}
                    </Typography>
                  </TableCell>
                  <TableCell>{s.sortOrder}</TableCell>
                  <TableCell>{s.isActive ? "بله" : "خیر"}</TableCell>
                  <TableCell align="left">
                    <IconButton
                      size="small"
                      color="primary"
                      aria-label="ویرایش"
                      onClick={() =>
                        setStatusDialog({
                          id: s.id,
                          title: s.title || "",
                          code: s.code || "",
                          sortOrder: s.sortOrder ?? 0,
                          isActive: !!s.isActive,
                        })
                      }
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    {isSystemStatus(s.code) ? (
                      <Tooltip title="وضعیت سیستمی — قابل حذف نیست">
                        <span>
                          <IconButton size="small" disabled aria-label="حذف">
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : (
                      <IconButton
                        size="small"
                        color="error"
                        aria-label="حذف"
                        onClick={() => confirmDeleteStatus(s)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              );
              })}
              {statuses.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary">
                      وضعیتی ثبت نشده است.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={!!tagDialog} onClose={() => setTagDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>ویرایش تگ</DialogTitle>
        <DialogContent>
          {tagDialog && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
              <TextField
                label="عنوان"
                fullWidth
                value={tagDialog.title}
                onChange={(e) => setTagDialog((d) => ({ ...d, title: e.target.value }))}
              />
              <TagColorField
                id="edit-consultation-tag-color"
                value={tagDialog.color}
                onChange={(c) => setTagDialog((d) => ({ ...d, color: c }))}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={tagDialog.isActive}
                    onChange={(e) => setTagDialog((d) => ({ ...d, isActive: e.target.checked }))}
                  />
                }
                label="فعال"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTagDialog(null)}>انصراف</Button>
          <Button variant="contained" onClick={saveTagDialog}>
            ذخیره
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!statusDialog} onClose={() => setStatusDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>ویرایش وضعیت</DialogTitle>
        <DialogContent>
          {statusDialog && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
              <TextField
                label="عنوان"
                fullWidth
                value={statusDialog.title}
                onChange={(e) => setStatusDialog((d) => ({ ...d, title: e.target.value }))}
              />
              <TextField
                label="Code (لاتین)"
                fullWidth
                disabled={isSystemStatus(statusDialog.code)}
                value={statusDialog.code}
                onChange={(e) => setStatusDialog((d) => ({ ...d, code: e.target.value }))}
                helperText={
                  isSystemStatus(statusDialog.code)
                    ? "کد وضعیت‌های سیستمی قابل تغییر نیست."
                    : undefined
                }
              />
              <TextField
                label="ترتیب نمایش"
                type="number"
                fullWidth
                value={statusDialog.sortOrder}
                onChange={(e) =>
                  setStatusDialog((d) => ({ ...d, sortOrder: parseInt(e.target.value, 10) || 0 }))
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={statusDialog.isActive}
                    onChange={(e) => setStatusDialog((d) => ({ ...d, isActive: e.target.checked }))}
                  />
                }
                label="فعال"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog(null)}>انصراف</Button>
          <Button variant="contained" onClick={saveStatusDialog}>
            ذخیره
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConsultationSettingsPage;
